import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { initializeGlobalErrorHandlers } from './services/errorHandling';
import { PostHogProvider, PostHogErrorBoundary } from 'posthog-js/react';
import { verifyPostHogHealth } from '@repo/shared-utils';
import { logger } from '@repo/shared-utils';

// Initialize global error handlers (window.onerror, unhandledrejection)
initializeGlobalErrorHandlers();

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error("Could not find root element to mount to");
}

// PostHog configuration
const posthogApiKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
const posthogHost = import.meta.env.VITE_PUBLIC_POSTHOG_HOST;

// Log PostHog configuration status
// In production, log errors/warnings to help debug initialization issues
const isDev = import.meta.env.DEV;
const isProduction = import.meta.env.PROD;

if (!posthogApiKey || !posthogHost) {
  // Always log warnings in production if PostHog isn't configured
  // This helps identify missing environment variables in Vercel
  if (isProduction) {
    logger.warn('[PostHog] Environment variables not set. PostHog will not initialize. Check Vercel environment variables.', {
      module: 'index',
      action: 'posthog_env_check',
      environment: 'production',
    });
  } else {
    logger.warn('PostHog: Environment variables not set. PostHog will not initialize.', {
      module: 'index',
      action: 'posthog_env_check',
      environment: 'development',
    });
  }
} else {
  // Log initialization in both dev and production (with less detail in production)
  if (isDev) {
    logger.info('PostHog: Configuration found, initializing...', {
      module: 'index',
      action: 'posthog_init_start',
      apiKey: posthogApiKey ? `${posthogApiKey.substring(0, 10)}...` : 'missing',
      host: posthogHost || 'missing'
    });
  } else {
    // Minimal production log to confirm env vars are present
    logger.info('[PostHog] Initializing with configured API key and host', {
      module: 'index',
      action: 'posthog_init_start',
      environment: 'production',
    });
  }
}

const posthogOptions = {
  api_host: posthogHost || 'https://us.i.posthog.com',
  defaults: '2025-05-24',
  // Enable error tracking in production
  capture_pageview: true,
  capture_pageleave: true,
  // Enable exception autocapture (automatically captures window.onerror and unhandled promise rejections)
  capture_exceptions: true,
} as const;

const root = ReactDOM.createRoot(rootElement);

// Fallback component for PostHogErrorBoundary
const ErrorFallback = ({ error, componentStack: _componentStack }: { error: unknown; componentStack: string; exceptionEvent?: unknown }) => {
  const errorMessage = error instanceof Error ? error.message : String(error);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-900 p-8 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-800 max-w-md text-center">
        <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Something went wrong</h1>
        <p className="text-slate-500 dark:text-slate-400 mb-6 text-sm">
          The application encountered an unexpected error. This error has been automatically reported.
        </p>
        <div className="bg-slate-100 dark:bg-slate-800 p-3 rounded-lg text-xs font-mono text-slate-600 dark:text-slate-300 mb-6 text-left overflow-auto max-h-32">
          {errorMessage}
        </div>
        <button
          onClick={() => {
            localStorage.clear();
            window.location.href = '/';
          }}
          className="w-full flex items-center justify-center bg-brand-600 hover:bg-brand-700 text-white py-3 rounded-xl font-bold transition shadow-md"
        >
          <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Clear Data & Restart
        </button>
      </div>
    </div>
  );
};

// Only wrap with PostHogProvider if API key is available
const AppWithPostHog = posthogApiKey ? (
  <PostHogProvider
    apiKey={posthogApiKey}
    options={posthogOptions}
  >
    <PostHogErrorBoundary fallback={ErrorFallback}>
      <App />
    </PostHogErrorBoundary>
  </PostHogProvider>
) : (
  <App />
);

// Log successful PostHog initialization and verify health
if (posthogApiKey) {
  if (isDev) {
    logger.info('[PostHog] Provider initialized successfully. PostHog is ready to track events.', {
      module: 'index',
      action: 'posthog_provider_ready',
    });

    // Wait a moment for PostHog to initialize, then verify health
    setTimeout(() => {
      const health = verifyPostHogHealth();
      if (health.isAvailable) {
        logger.debug('[PostHog] Initialization verification', {
          module: 'index',
          action: 'posthog_health_check',
          canCapture: health.canCapture,
          canCaptureException: health.canCaptureException,
          distinctId: health.distinctId,
          hasOptedIn: health.hasOptedIn,
          hasOptedOut: health.hasOptedOut,
        });
      } else {
        logger.warn('[PostHog] Initialization verification failed', {
          module: 'index',
          action: 'posthog_health_check',
          error: health.error,
        });
      }
    }, 2000);
  } else {
    logger.info('[PostHog] Provider initialized. Event tracking enabled.', {
      module: 'index',
      action: 'posthog_provider_ready',
      environment: 'production',
    });

    // In production, verify health after a delay and log warnings if needed
    setTimeout(() => {
      const health = verifyPostHogHealth();
      if (!health.isAvailable || !health.canCaptureException) {
        logger.warn('[PostHog] Health check failed after initialization', {
          module: 'index',
          action: 'posthog_health_check',
          isAvailable: health.isAvailable,
          canCaptureException: health.canCaptureException,
          error: health.error,
        });
      }
    }, 2000);
  }
} else {
  // Log warning if PostHog is not configured
  if (isProduction) {
    logger.warn(
      '[PostHog] Not initialized - error tracking disabled. ' +
      'Set VITE_PUBLIC_POSTHOG_KEY and VITE_PUBLIC_POSTHOG_HOST environment variables.',
      {
        module: 'index',
        action: 'posthog_disabled',
        environment: 'production',
      }
    );
  }
}

root.render(
  <React.StrictMode>
    <ErrorBoundary>
      {AppWithPostHog}
    </ErrorBoundary>
  </React.StrictMode>
);
